import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { authenticated, login, onKeycloakReady, updateToken } from '@/plugins/keycloak';
import { clearUrlState, trailingSlash } from '@/utils/urlUtils';
import { useMessagesStore } from '@/store/messagesStore';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/landing',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/',
    redirect: '/dashboard/overview',
  },
  {
    path: '/dashboard/:view?',
    name: 'dashboardView',
    component: () => import('@/views/statistics/DashboardView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/dashboard/assets/managerial-report',
    name: 'ManagerialReport',
    component: () => import('@/views/reports/ManagerialReportView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/dashboard/assets/assetGroupStatistics/:agUid/:rc?',
    name: 'AssetGroupStatistics',
    component: () => import('@/views/statistics/AssetGroupStatistics.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/dashboard/measures/measuresGroupStatistics/:mgUid',
    name: 'MeasureGroupStatistics',
    component: () => import('@/views/statistics/MeasureGroupStatistics.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/dashboard/measures/MeasureGroupWithImplementationsStatistics/:mgUid',
    name: 'MeasureGroupWithImplementationStatistics',
    component: () => import('@/views/statistics/MeasureGroupWithImplementationsStatistics.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/groupable/:groupType/groups/',
    component: () => import('@/views/DefaultLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'Groups',
        component: () => import('@/views/catalogue/GroupsView.vue'),
      },
      {
        path: 'list/:groupIdentifier',
        name: 'GroupDetail',
        component: () => import('@/views/catalogue/GroupDetailView.vue'),
        meta: {},
      },
      {
        path: ':action/:groupIdentifier?',
        name: 'GroupForm',
        component: () => import('@/views/catalogue/GroupFormView.vue'),
        meta: {},
      },
      {
        path: ':groupIdentifier/item/:action/:itemIdentifier?',
        name: 'ItemForm',
        component: () => import('@/views/catalogue/GroupItemFormView.vue'),
        meta: {},
      },
      {
        path: ':groupIdentifier/assetclone/:itemIdentifier',
        name: 'AssetCloneForm',
        component: () => import('@/views/catalogue/AssetCloneFormView.vue'),
        meta: {},
      },
      {
        path: ':groupIdentifier/implementation/:measureIdentifier/:action/:itemIdentifier?',
        name: 'MeasureImplementationForm',
        component: () => import('@/views/catalogue/MeasureImplementationFormView.vue'),
        meta: {},
      },
    ],
  },
  {
    path: '/catalogue',
    component: () => import('@/views/DefaultLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'impacts',
        name: 'Impacts',
        component: () => import('@/views/catalogue/ImpactsView.vue'),
        meta: {},
      },
      {
        path: 'impacts/:action/:impactId?',
        name: 'ImpactForm',
        component: () => import('@/views/catalogue/ImpactFormView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'threat-sources',
        name: 'ThreatSources',
        component: () => import('@/views/catalogue/ThreatSourceView.vue'),
        meta: {},
      },
      {
        path: 'threat-sources/:action/:tsId?',
        name: 'ThreatSourceForm',
        component: () => import('@/views/catalogue/ThreatSourceFormView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'threat-source-types',
        name: 'ThreatSourceTypes',
        component: () => import('@/views/catalogue/ThreatSourceTypeView.vue'),
        meta: {},
      },
      {
        path: 'threat-source-types/:action/:tstId?',
        name: 'ThreatSourceTypeForm',
        component: () => import('@/views/catalogue/ThreatSourceTypeFormView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'imp-exp',
        name: 'ImportExport',
        component: () => import('@/views/catalogue/ImportExportView.vue'),
        meta: {},
      },
      {
        path: 'imp-exp/help',
        name: 'ImpExpHelp',
        component: () => import('@/views/static/ImportExportHelpView.vue'),
        meta: {},
      },
    ],
  },
  {
    path: '/risk-analyse',
    component: () => import('@/views/DefaultLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'risk-acceptance-decisions',
        name: 'RiskAcceptanceDecisions',
        component: () =>
          import('@/views/riskAnalyse/riskAcceptanceDecisions/RiskAcceptanceDecisionsView.vue'),
        meta: {},
      },
      {
        path: 'risk-acceptance-decisions/:action/:identifier?',
        name: 'RiskAcceptanceDecisionsForm',
        component: () =>
          import('@/views/riskAnalyse/riskAcceptanceDecisions/RiskAcceptanceDecisionsFormView.vue'),
        meta: {},
      },
      {
        path: 'risks/:assetId?/:avId?',
        name: 'Risks',
        component: () => import('@/views/riskAnalyse/risks/RisksView.vue'),
        meta: {},
      },
      {
        path: 'risk/:assetId/:avId/:tgId/:tId/:action/:riskId?',
        name: 'RiskForm',
        component: () => import('@/views/riskAnalyse/risks/RiskFormView.vue'),
        meta: {},
      },
      {
        path: 'risk/:assetId/:avId/:tgId/:tId/add/:groupType/:groupIdentifier/:measureIdentifier/:action/:itemIdentifier?',
        name: 'AddRiskFormMeasureImplementationAdd',
        component: () => import('@/views/catalogue/MeasureImplementationFormView.vue'),
        meta: {},
      },
      {
        path: 'risk/:assetId/:avId/:tgId/:tId/edit/:riskId/:groupType/:groupIdentifier/:measureIdentifier/:action/:itemIdentifier?',
        name: 'EditRiskFormMeasureImplementationAdd',
        component: () => import('@/views/catalogue/MeasureImplementationFormView.vue'),
        meta: {},
      },
      {
        path: 'manage/:assetGroupId?/:assetId?/:riskId?',
        name: 'RisksManagement',
        component: () => import('@/views/riskAnalyse/risks/RisksManagementView.vue'),
        meta: {},
      },
      {
        path: 'manage/:assetGroupId/:assetId/:riskId/:groupType/:groupIdentifier/:measureIdentifier/:action/:itemIdentifier?',
        name: 'RisksManagementMeasureImplementationAdd',
        component: () => import('@/views/catalogue/MeasureImplementationFormView.vue'),
        meta: {},
      },
    ],
  },
  {
    path: '/report/:type/:reportBy?',
    name: 'ReportView',
    component: () => import('@/views/reports/RiskAnalyseReportView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/treatment/risk',
    name: 'RiskTreatmentView',
    component: () => import('@/views/treatment/RiskTreatmentView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/users',
    name: 'Users',
    component: () => import('@/views/users/UsersView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/users/:action/:userId?',
    name: 'UserForm',
    component: () => import('@/views/users/UserFormView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/confidentiality-levels',
    name: 'ConfidentialityLevels',
    component: () => import('@/views/confidentialityLevels/ConfidentialityLevelsView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/confidentiality-levels/:action/:confidentialityLevelId?',
    name: 'ConfidentialityLevelForm',
    component: () => import('@/views/confidentialityLevels/ConfidentialityLevelFormView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/document-references',
    name: 'DocumentReferences',
    component: () => import('@/views/documentReferences/DocumentReferencesView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/document-references/:action/:documentReferenceId?',
    name: 'DocumentReferenceForm',
    component: () => import('@/views/documentReferences/DocumentReferenceFormView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/licences',
    name: 'Licences',
    component: () => import('@/views/licences/LicencesView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/licences/:action',
    name: 'LicencesForm',
    component: () => import('@/views/licences/LicencesFormView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/settings',
    name: 'SubjectSettings',
    component: () => import('@/views/settings/SubjectSettingsView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/settings/tags',
    name: 'Tags',
    component: () => import('@/views/settings/TagsView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/settings/tags/:action/:tagIdentifier?',
    name: 'TagForm',
    component: () => import('@/views/settings/TagFormView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
];
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});
router.beforeEach(async (to, from, next) => {
  document.title = 'RiA';
  try {
    await onKeycloakReady();
  } catch (error) {
    console.error('Error initializing Keycloak:', error);
    next('/');
    return;
  }
  window.addEventListener('beforeunload', function () {
    window.localStorage.setItem('refreshing', 'true');
  });
  const postRefresh = window.localStorage.getItem('refreshing');
  if (postRefresh) window.localStorage.removeItem('refreshing');
  else window.localStorage.setItem('previousRoute', from.path);
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const isAuthenticated = authenticated();
  if (requiresAuth && !isAuthenticated) {
    await login();
    return;
  } else {
    try {
      await updateToken(1500);
    } catch (error) {
      console.warn('Unable to refresh the Keycloak token, redirecting to login:', error);
      await login();
      return;
    }
    const selectedSubjectIdentifier = localStorage.getItem('selectedSubjectIdentifier');
    if (!selectedSubjectIdentifier && to.name !== 'landing') {
      next('/landing/');
    } else {
      if (to.path.endsWith('/')) {
        next();
      } else {
        next(trailingSlash(to.path));
      }
    }
  }
});
router.afterEach(() => {
  clearUrlState();
  useMessagesStore().toastUnshowedMessages();
});
export default router;
